/** Customer-facing specifications. Never read today's inventory for an old invoice. */
export interface InvoiceSpecSnapshot {
  item_type: string;
  ram_snapshot?: string | null;
  storage_snapshot?: string | null;
  cpu_snapshot?: string | null;
  gpu_snapshot?: string | null;
  touchscreen_snapshot?: boolean | null;
  hardware_snapshot_recorded?: boolean;
  specs_snapshot?: string | null;
}

/** Only named dedicated GPU families qualify; generic Radeon/Intel graphics do not. */
export function discreteGpuModel(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const models = value.split(/\s*(?:\+|;|\||,|\/|&)\s*/).filter(part => {
    if (/^(?:no|none|unknown|integrated|onboard)\b/i.test(part.trim()) || !/\d/.test(part)) return false;
    return /\b(?:nvidia|geforce|quadro|rtx|gtx|mx|nvs|firepro)\b/i.test(part)
      || /\bradeon\s+(?:rx\b|pro\b|[rm][579]\s*[- ]?m\d|hd\s+[5-8]\d{3}m\b|[456]\d{2}\b)/i.test(part)
      || /\bintel\s+arc\s+[ab]\d{3}m\b/i.test(part);
  });
  return models.map(part => part.trim()).filter(Boolean).join(' + ') || null;
}

export function formatInvoiceSpecs(item: InvoiceSpecSnapshot): string {
  if (item.item_type !== 'laptop') return '';
  const legacy = (item.specs_snapshot || '').split(/\s*[•·|]\s*/).map(s => s.trim()).filter(Boolean);
  const hasSnapshots = item.hardware_snapshot_recorded === true;
  const cpu = hasSnapshots ? item.cpu_snapshot : item.cpu_snapshot || legacy.find(s =>
    /\b(?:core\s+)?i[3579][ -]?\d|\b(?:intel|amd|ryzen|celeron|pentium|xeon|athlon|snapdragon|apple\s+m[1-9])\b/i.test(s)
    && !/\b(?:graphics|radeon|geforce|nvidia|iris|uhd|arc|quadro)\b/i.test(s));
  const ram = item.ram_snapshot ?? legacy.find(s => /\b(?:ram|ddr[345])\b/i.test(s) || /^\d+\s*gb$/i.test(s));
  const storage = item.storage_snapshot ?? legacy.find(s => /\b(?:ssd|hdd|nvme|emmc|storage)\b/i.test(s));
  const gpu = discreteGpuModel(hasSnapshots ? item.gpu_snapshot : item.gpu_snapshot || legacy.find(s => discreteGpuModel(s)));
  const touch = hasSnapshots || item.touchscreen_snapshot != null
    ? item.touchscreen_snapshot === true
    : legacy.some(s => /^(?:touch|touchscreen|touch screen)$/i.test(s));
  return [cpu, ram, storage, gpu, touch ? 'Touchscreen' : null].filter(s => s?.trim()).join(' • ');
}
